<script setup lang="ts">
import {computed, ref} from 'vue'
import {Check, ChevronsUpDown} from 'lucide-vue-next'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command'

type TimeZoneOption = {
  value: string
  label: string
}

const props = defineProps<{
  id?: string
  modelValue: string
  options: TimeZoneOption[]
  placeholder: string
  searchPlaceholder: string
  emptyText: string
}>()
const emit = defineEmits<{'update:modelValue': [value: string]}>()
const open = ref(false)
const selected = computed(() => props.options.find((option) => option.value === props.modelValue))

function choose(value: string): void {
  emit('update:modelValue', value)
  open.value = false
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        :id="id"
        variant="outline"
        role="combobox"
        data-testid="settings-timezone-trigger"
        :aria-expanded="open"
        class="w-full justify-between text-left font-normal">
        <span class="truncate">{{ selected?.label || modelValue || placeholder }}</span>
        <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[min(34rem,calc(100vw-2rem))] p-0">
      <Command>
        <CommandInput :placeholder="searchPlaceholder" />
        <CommandEmpty>{{ emptyText }}</CommandEmpty>
        <CommandList class="max-h-72">
          <CommandGroup>
            <CommandItem
              v-for="option in options"
              :key="option.value"
              :value="option.label"
              @select="choose(option.value)">
              <Check class="mr-2 size-4" :class="modelValue === option.value ? 'opacity-100' : 'opacity-0'" />
              <span class="truncate">{{ option.label }}</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
